import { AppreciationPrice, AppreciationInvitation } from "./ReaderAppreciation";
import { displayLabel } from "../../utils/displayLabel";
import TbrButton from "../ui/TbrButton";
import BookRating from "./BookRating";
// Market.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getCurrentReader } from "../../auth/getCurrentReader";
import { Link, useNavigate } from "react-router-dom";
import PublicHeader from "./PublicHeader";
import { persistAuthType } from "../../auth/authTypeStorage";
import GenreHeader from "./GenreHeader";
import "./MarketLibrary.css";
import { followGenre, getReaderFollowGraph, unfollowGenre } from "../../services/followService";

const API_URL = "https://bookish-master-data.s3.us-west-2.amazonaws.com/market.json";
const PLACEHOLDER = "https://placehold.co/320x480?text=No+Image";

const getNumericPrice = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const sortFormatsByPrice = (formats = []) =>
  [...formats].sort((a, b) => {
    const priceDiff = getNumericPrice(a?.price) - getNumericPrice(b?.price);
    if (priceDiff !== 0) return priceDiff;
    return String(a?.type || "").localeCompare(String(b?.type || ""));
  });

// put this near the top of the file
const getAuthorShopUrl = (book) => {
  if (book?.subdomain) {
    const host = String(book.subdomain)
      .replace(/^https?:\/\//, "")
      .replace(/\/.*/, "");
    return `https://${host}/`;
  }
  if (book?.productUrl) {
    try {
      const u = new URL(book.productUrl);
      return `${u.protocol}//${u.hostname}/`;
    } catch {}
  }
  return null;
};

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const toProductUrl = (book) => {
  if (book?.productUrl) return book.productUrl;
  return `/shop/${slugify(book?.slug || book?.title || "book")}`;
};

const toInternalPath = (url) => {
  if (!url || typeof window === "undefined") return null;

  if (typeof url === "string" && url.startsWith("/")) {
    return url;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.host === window.location.host) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {}

  return null;
};

/* ---------- Canonical Genres (match AddBook.js) ---------- */
const GENRE_TREE = {
  Fiction: {
    main: [
      "Children's Books",
      "Fantasy",
      "Romantasy",
      "Literary Fiction",
      "Historical Fiction",
      "Horror",
      "Mystery / Crime",
      "Thriller / Suspense",
      "Romance",
      "Science Fiction",
      "Western",
    ],
    sub: {
      Fantasy: [
        "Dark Fantasy",
        "High / Epic Fantasy",
        "Urban Fantasy",
        "Fairytales & Folklore",
        "Wizards & Witches",
      ],
      Romantasy: ["Dark Romantasy", "Fae Romantasy", "Paranormal Romantasy"],
      "Science Fiction": ["Space Opera", "Dystopian", "Cyberpunk", "Steampunk"],
      Horror: ["Gothic Horror", "Paranormal", "Psychological Horror"],
      "Mystery / Crime": ["Detective", "Cozy Mystery", "Police Procedural"],
      "Thriller / Suspense": ["Spy Thriller", "Political Thriller", "Psychological Thriller"],
      Romance: ["Contemporary Romance", "Historical Romance", "Paranormal Romance"],
      "Historical Fiction": ["War Stories", "Biographical Fiction"],
      Western: ["Classic Western", "Weird West"],
      "Literary Fiction": [],
    },
  },
  "Non-Fiction": {
    main: [
      "Biography / Memoir",
      "History",
      "Self-Help / Personal Development",
      "Religion / Spirituality",
      "Science / Nature",
      "Travel / Adventure",
      "True Crime",
      "Essays",
    ],
    sub: {
      "Biography / Memoir": ["Celebrity Memoir", "Literary Memoir"],
      History: ["Military History", "Cultural History"],
      "Self-Help / Personal Development": ["Habits", "Productivity", "Relationships"],
      "Religion / Spirituality": ["Inspirational", "Comparative Religion"],
      "Science / Nature": ["Popular Science", "Environment"],
      "Travel / Adventure": ["Travelogue", "Exploration"],
      "True Crime": ["Case Study", "Investigative"],
      Essays: [],
    },
  },
};

// Maps for fast canonical lookups
const GENRE_ALIASES = {
  romantasy: "Romantasy",
  "romantic fantasy": "Romantasy",
  "fantasy romance": "Romantasy",
};

const CANON_MAIN_LC_TO_DISPLAY = (() => {
  const m = new Map();
  [...GENRE_TREE["Fiction"].main, ...GENRE_TREE["Non-Fiction"].main].forEach((g) =>
    m.set(g.toLowerCase(), g)
  );
  Object.entries(GENRE_ALIASES).forEach(([alias, display]) => m.set(alias, display));
  return m;
})();

const CANON_SUB_BY_MAIN_LC = (() => {
  const map = new Map(); // main(lc) -> Map(sub lc -> display)
  for (const branch of ["Fiction", "Non-Fiction"]) {
    for (const [main, subs] of Object.entries(GENRE_TREE[branch].sub)) {
      const mm = main.toLowerCase();
      if (!map.has(mm)) map.set(mm, new Map());
      const subMap = map.get(mm);
      subs.forEach((sg) => subMap.set(sg.toLowerCase(), sg));
    }
  }
  return map;
})();

/* ---------- Helpers ---------- */
const asArray = (v) => (Array.isArray(v) ? v : v == null || v === "" ? [] : [v]);
const clean = (s) => String(s ?? "").trim();

const extractGenresFromBook = (b) => {
  const g = new Set();
  // book-level
  asArray(b?.genres).forEach((x) => g.add(clean(x)));
  asArray(b?.genre).forEach((x) => g.add(clean(x)));
  // format-level (fallback)
  (b?.formats || []).forEach((f) => {
    asArray(f?.genres).forEach((x) => g.add(clean(x)));
    asArray(f?.genre).forEach((x) => g.add(clean(x)));
  });
  g.delete("");
  g.delete("undefined");
  g.delete("null");
  return Array.from(g);
};

const extractSubgenresFromBook = (b) => {
  const s = new Set();
  asArray(b?.subgenres).forEach((x) => s.add(clean(x)));
  s.delete("");
  s.delete("undefined");
  s.delete("null");
  return Array.from(s);
};

// Normalize a main genre to canonical display (or null if not canonical)
const normalizeMain = (g) => {
  const disp = CANON_MAIN_LC_TO_DISPLAY.get(clean(g).toLowerCase());
  return disp ?? null;
};

// Normalize a subgenre to canonical display for a given main (fallback to original if not found)
const normalizeSubForMain = (sg, mainDisplay) => {
  const subMap = CANON_SUB_BY_MAIN_LC.get(clean(mainDisplay).toLowerCase());
  if (!subMap) return null;
  const disp = subMap.get(clean(sg).toLowerCase());
  return disp ?? null;
};

// maps display genre → tailwind color key
const GENRE_COLOR_KEY = {
  "Children's Books": "children",
  Fantasy: "fantasy",
  Romantasy: "romantasy",
  "Science Fiction": "scifi",
  Horror: "horror",
  Romance: "romance",
  Western: "western",
  "Historical Fiction": "historical",
  "Literary Fiction": "literaryFiction",
  "Mystery / Crime": "mystery",
  "Thriller / Suspense": "thriller",
  "Biography / Memoir": "nonfiction",
  History: "historical",
  "Self-Help / Personal Development": "nonfiction",
  "Religion / Spirituality": "christian",
  "Science / Nature": "nonfiction",
  "Travel / Adventure": "nonfiction",
  "True Crime": "crime",
  Essays: "nonfiction",
  "Non-Fiction": "nonfiction",
};

const canonicalGenreToUiKey = (genre) => GENRE_COLOR_KEY[genre] || null;

export default function Market() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters (no tabs)
  const [freeOnly, setFreeOnly] = useState(false);
  const [genreBranch, setGenreBranch] = useState("Fiction"); // default
  const [activeGenre, setActiveGenre] = useState(() => normalizeMain(new URLSearchParams(window.location.search).get("genre")) || null);
  const [selectedGenre, setSelectedGenre] = useState(() => canonicalGenreToUiKey(normalizeMain(new URLSearchParams(window.location.search).get("genre"))) || null);
  const [joinedGenres, setJoinedGenres] = useState({});
  const [selectedSubgenres, setSelectedSubgenres] = useState(new Set()); // canonical display strings
  const [searchQuery, setSearchQuery] = useState("");

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [readerChecking, setReaderChecking] = useState(true);
  const [followMessage, setFollowMessage] = useState("");
  const [joinMutationPending, setJoinMutationPending] = useState(false);
  const navigate = useNavigate();
  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24); // sensible default: 24 per page

  // mobile drawer state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const filterPanel = useRef(null);
  const filterTrigger = useRef(null);
  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const trigger = filterTrigger.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = event => {
      if (event.key === "Escape") setMobileFiltersOpen(false);
      if (event.key !== "Tab") return;
      const controls = filterPanel.current?.querySelectorAll('button:not([disabled]), a[href], input, select');
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKey);
      trigger?.focus();
    };
  }, [mobileFiltersOpen]);
  const [catalogError, setCatalogError] = useState("");

  // reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [freeOnly, activeGenre, selectedSubgenres, searchQuery]);

  /* ---------- Fetch ---------- */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}?v=${Date.now()}`, { cache: "no-store" });
        if (res.ok === false) throw new Error("Catalog unavailable");
        const data = await res.json();
        setBooks(Array.isArray(data?.books) ? data.books : []);
      } catch (err) {
        console.error("❌ Failed to fetch books:", err);
        setBooks([]);
        setCatalogError("We could not load the books. Please refresh and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    let active = true;
    const loadSessionAndFollows = async () => {
      try {
        const reader = await getCurrentReader();
        if (!active) return;
        setIsLoggedIn(Boolean(reader));
        if (!reader) { setJoinedGenres({}); return; }
        // A follow API failure must not invalidate a valid reader session.
        const graph = await getReaderFollowGraph();
        const followedGenres = graph.genres;
        const normalized = {};
        followedGenres.map((g) => normalizeMain(g.name || g.id)).filter(Boolean).forEach((canon) => {
          normalized[canon] = true;
        });
        if (active) setJoinedGenres(normalized);
      } catch {
        if (active) setFollowMessage("Could not load your genre follows. You can try joining a circle again.");
      } finally {
        if (active) setReaderChecking(false);
      }
    };
    loadSessionAndFollows();
    return () => { active = false; };
  }, []);

  /* ---------- Aggregations ---------- */
  const isGenreInBranch = (genreDisplay, branch) => {
    return GENRE_TREE[branch]?.main.includes(genreDisplay);
  };

  // All canonical main genres present in data (deduped, sorted)
  const allGenres = useMemo(() => {
    const seen = new Map(); // lc -> display

    (books || []).forEach((b) => {
      extractGenresFromBook(b).forEach((raw) => {
        const disp = normalizeMain(raw);
        if (!disp) return;

        // 👇 THIS is the missing link
        if (!isGenreInBranch(disp, genreBranch)) return;

        seen.set(disp.toLowerCase(), disp);
      });
    });

    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [books, genreBranch]);


  // Canonical subgenres available for the currently active canonical main
  const subgenresForActive = useMemo(() => {
    if (!activeGenre) return [];
    const present = new Map(); // lc -> display
    (books || []).forEach((b) => {
      // only consider books that have the active main genre
      const mains = extractGenresFromBook(b).map(normalizeMain).filter(Boolean);
      if (!mains.includes(activeGenre)) return;
      extractSubgenresFromBook(b).forEach((sgRaw) => {
        const disp = normalizeSubForMain(sgRaw, activeGenre);
        if (disp) present.set(disp.toLowerCase(), disp);
      });
    });
    return Array.from(present.values()).sort((a, b) => a.localeCompare(b));
  }, [books, activeGenre]);

  /* ---------- Filtering ---------- */

  const filtered = useMemo(() => {
    const isFree = (b) => (b.formats || []).some((f) => Number(f.price) === 0);

    return (books || []).filter((b) => {
      const q = displayLabel(searchQuery).toLowerCase();

      if (q) {
        const haystack = [
          b.title,
          b.authorName,
          ...(b.genres || []),
          ...(b.subgenres || []),
        ]
          .map(displayLabel)
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(q)) return false;
      }

      if (freeOnly && !isFree(b)) return false;

      // main genre (canonical)
      if (activeGenre) {
        const mains = extractGenresFromBook(b).map(normalizeMain).filter(Boolean);
        if (!mains.includes(activeGenre)) return false;
      }

      // subgenres (canonical, OR across selected)
      if (selectedSubgenres.size > 0) {
        const subs = new Set(
          extractSubgenresFromBook(b)
            .map((sg) => (activeGenre ? normalizeSubForMain(sg, activeGenre) : null))
            .filter(Boolean)
        );
        let any = false;
        for (const want of selectedSubgenres) {
          if (subs.has(want)) {
            any = true;
            break;
          }
        }
        if (!any) return false;
      }

      return true;
    });
  }, [books, freeOnly, activeGenre, selectedSubgenres, searchQuery]);

  /* ---------- Pagination ---------- */
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const paged = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const goTo = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  /* ---------- Handlers ---------- */

  const toggleSubgenre = (sg) => {
    setSelectedSubgenres((prev) => {
      const next = new Set(prev);
      next.has(sg) ? next.delete(sg) : next.add(sg);
      return next;
    });
  };

  const clearSubgenres = () => setSelectedSubgenres(new Set());

  const selectGenre = (g) => {
    setFollowMessage("");
    setActiveGenre((prev) => (prev === g ? null : g));
    setSelectedGenre((prev) => (prev === g ? null : GENRE_COLOR_KEY[g] || null));
    setSelectedSubgenres(new Set());
  };

  const clearGenre = () => {
    setActiveGenre(null);
    setSelectedGenre(null);
    setSelectedSubgenres(new Set());
  };

  const clearAllFilters = () => {
    setFreeOnly(false);
    setActiveGenre(null);
    setSelectedGenre(null);
    setSelectedSubgenres(new Set());
  };

  /* ---------- UI ---------- */

  return (
    <div className="market-page min-h-screen bg-bookish-lightCream text-bookish-purple">
      <PublicHeader />
      <div className="market-toolbar"><h1>Find your next favorite book.</h1><div className="market-search-row"><input type="search" aria-label="Search books by title, author, or trope" placeholder="Search title, author, trope…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /><button ref={filterTrigger} className="lg:hidden" onClick={() => setMobileFiltersOpen(true)}>Filters</button></div>{isLoggedIn && <Link className="public-text-link" to="/readerportal">My reader account →</Link>}</div>
      {/* MAIN */}
      <main className="grid grid-cols-1 lg:grid-cols-[200px_minmax(0,1fr)] items-start gap-x-6 px-4 sm:px-6 lg:px-8 mt-4 pb-12 max-w-[1440px] mx-auto">
        {/* LEFT: sidebar */}
        <aside className="hidden lg:block lg:sticky lg:top-[105px] h-fit p-4
          bg-[#F6EEDD] border-r border-bookish-purple/15">



          <div className="relative z-10">


            <div className="flex items-center justify-between mb-1">
              <h3 className="text-medium font-semibold text-bookish-purple">Filters</h3>
              <button
                onClick={clearAllFilters}
                className="text-sm text-bookish-purpleAccent hover:underline"
              >
                Clear filters
              </button>
            </div>
          {/* Genres */}
         <div className="mt-4 rounded-lg bg-white/70 p-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-bookish-purple/70 mb-2">
            Genres
          </h2>

          {/* Fiction / Non-Fiction toggle */}
          <div className="flex gap-2 mb-3">
            {["Fiction", "Non-Fiction"].map((branch) => {
              const active = genreBranch === branch;
              return (
                <button
                  key={branch}
                  onClick={() => {
                    setGenreBranch(branch);
                    setActiveGenre(null);
                    setSelectedGenre(null);
                    setSelectedSubgenres(new Set());
                  }}
                  className={`flex-1 px-1 py-2 rounded-md text-sm border ${
                    active
                      ? "bg-bookish-purple text-white border-bookish-purple"
                      : "bg-white text-bookish-purple border-bookish-purple/20 hover:border-bookish-purple/40"
                  }`}
                >
                  {branch}
                </button>
              );
            })}
          </div>
          {/* Main genres for selected branch */}
          {/* Genres with inline subgenres */}
          <div className="space-y-1 max-h-[40vh] overflow-auto pr-1">
            {allGenres.length === 0 ? (
              <div className="text-sm text-bookish-purple/60">No genres yet.</div>
            ) : (
              allGenres.map((g) => {
                const active = activeGenre === g;
                const colorKey = GENRE_COLOR_KEY[g] || "bookishCircle";

                return (
                  <div key={g}>
                    {/* GENRE ROW */}
                    <button
                      onClick={() => selectGenre(g)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                        active
                          ? "bg-bookish-purple/10 text-bookish-purple font-medium"
                          : "text-bookish-purple/80 hover:bg-bookish-purple/5"
                      }`}
                    >
                      {/* genre ring */}
                      <span
                        className={`h-3 w-3 rounded-full border-2
                          border-genreRings-${colorKey}
                          ${active ? `bg-genres-${colorKey}` : "bg-transparent"}
                        `}
                      />
                      <span>{displayLabel(g)}</span>
                    </button>

                    {/* SUBGENRES — nested directly under */}
                    {active && subgenresForActive.length > 0 && (
                      <div className="ml-7 mt-1 space-y-1">
                        {subgenresForActive.map((sg) => {
                          const subActive = selectedSubgenres.has(sg);
                          return (
                            <button
                              key={sg}
                              onClick={() => toggleSubgenre(sg)}
                              className={`w-full text-left px-3 rounded-md text-sm ${
                                subActive
                                  ? "bg-bookish-purple/10 text-bookish-purple font-medium"
                                  : "text-bookish-purple/70 hover:bg-bookish-purple/5"
                              }`}
                            >
                              {displayLabel(sg)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          </div>

          <button
              disabled
              title="Sign up to save books"
              className="w-full text-left mt-2 px-3 py-2 rounded-md mb-1 border bg-bookish-cream text-bookish-purple/50 italic cursor-not-allowed"
            >
              My TBR (sign up)
            </button>
          </div>

          <button
            onClick={() => setFreeOnly((v) => !v)}
            aria-pressed={freeOnly}
            className={`w-full text-left px-3 py-2 rounded-md mb-1 border ${
              freeOnly
                ? "bg-bookish-purple text-white border-bookish-purple"
                : "bg-white text-bookish-purple border-bookish-purple/20 hover:border-bookish-purple/40"
            }`}
          >
            Free Books
          </button>
          <div className="h-px bg-bookish-purple/10 my-4" />
        </aside>

        {/* RIGHT: content */}
        <section>
          {/* Top controls: results + page size */}
          <div className="mb-0 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-bookish-purple/70">{loading ? "Loading books…" : catalogError ? "Books unavailable" : filtered.length + " results"}</span>
              <label className="inline-flex items-center gap-2">
                <span className="text-bookish-purple/70">Per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-md border border-bookish-purple/20 bg-white px-2 py-1"
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                </select>
              </label>
            </div>
          </div>

          {!readerChecking && !isLoggedIn && <AppreciationInvitation />}
          {/* Selected filters row (chips) */}
          <div className="flex justify-between items-start mb-4">

          {/* LEFT: chips */}
          <div className="flex flex-wrap items-center gap-2">
            {!activeGenre && selectedSubgenres.size === 0 && !freeOnly ? (
              <span className="text-sm text-bookish-purple/60">{/* empty */}</span>
            ) : (
              <>
                {freeOnly && (
                  <button
                    onClick={() => setFreeOnly(false)}
                    className="text-sm rounded-full border border-bookish-purple/20 px-3 py-1 bg-white hover:border-bookish-purple/40"
                  >
                    Free <span className="ml-1">×</span>
                  </button>
                )}

                {activeGenre && (
                  <button
                    onClick={clearGenre}
                    className="text-sm rounded-full border border-bookish-purple/20 px-3 py-1 bg-white hover:border-bookish-purple/40"
                  >
                    {displayLabel(activeGenre)} <span className="ml-1">×</span>
                  </button>
                )}

                {Array.from(selectedSubgenres).map((sg) => (
                  <button
                    key={sg}
                    onClick={() => toggleSubgenre(sg)}
                    className="text-sm rounded-full border border-bookish-purple/20 px-3 py-1 bg-white hover:border-bookish-purple/40"
                  >
                    {displayLabel(sg)} <span className="ml-1">×</span>
                  </button>
                ))}

                <button
                  onClick={clearAllFilters}
                  className="text-xs text-bookish-purpleAccent hover:underline ml-1"
                >
                  Clear all
                </button>
              </>
            )}
          </div>

          {/* RIGHT: circle */}
          <div className="flex-shrink-0">
            <GenreHeader
              genre={selectedGenre}
              joined={!!joinedGenres[activeGenre]}
              genreLabel={activeGenre}
              disabled={joinMutationPending || readerChecking}
              onToggleJoin={async () => {
                if (!selectedGenre || joinMutationPending || readerChecking) return;
                const canonicalGenre = activeGenre;
                if (!canonicalGenre) return;
                setJoinMutationPending(true);
                setFollowMessage("");
                try {
                  const reader = await getCurrentReader();
                  setIsLoggedIn(Boolean(reader));
                  if (!reader) {
                    persistAuthType("reader");
                    const returnTo = `/market?genre=${encodeURIComponent(canonicalGenre)}`;
                    navigate(`/loginreader?returnTo=${encodeURIComponent(returnTo)}`);
                    return;
                  }
                  const wasJoined = !!joinedGenres[canonicalGenre];
                  if (wasJoined) {
                    await unfollowGenre(canonicalGenre);
                  } else {
                    await followGenre(canonicalGenre);
                  }
                  setJoinedGenres((prev) => ({ ...prev, [canonicalGenre]: !wasJoined }));
                  setFollowMessage(wasJoined ? `You left the ${canonicalGenre} Circle.` : `You joined the ${canonicalGenre} Circle.`);
                } catch (err) {
                  setFollowMessage("Your genre follow could not be saved. Please try again.");
                } finally {
                  setJoinMutationPending(false);
                }
              }}
            />
            {followMessage && <p role="status" className="mt-2 max-w-xs text-sm text-bookish-purple">{followMessage}</p>}
          </div>

        </div>

          {/* Grid */}
          {loading ? (
            <GridSkeleton />
          ) : catalogError ? (<p role="alert" className="rounded-lg border p-6">{catalogError}</p>) : paged.length === 0 ? (
            <div className="bg-white border border-bookish-purple/10 rounded-xl p-4 text-center">
              <p className="text-bookish-purple/70">No books match your filters.</p>
            </div>
          ) : (
            <>
              <div className="mx-auto max-w-6xl">

                <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 items-stretch">
                  {paged.map((book, idx) => (
                    <BookCard key={`${book.title}-${idx}`} book={book} />
                  ))}
                </div>
              </div>

              {/* Pagination controls */}
              <Pagination page={currentPage} totalPages={totalPages} goTo={goTo} />
            </>
          )}
        </section>
      </main>

      {/* MOBILE FILTER DRAWER */}
      {mobileFiltersOpen && (
        <div className="lg:hidden fixed inset-0 z-50" onKeyDown={e => { if (e.key === "Escape") setMobileFiltersOpen(false); }}>
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileFiltersOpen(false)}
            aria-hidden="true"
          />
          {/* panel */}
          <div ref={filterPanel} role="dialog" aria-modal="true" aria-label="Book filters" className="absolute top-0 right-0 h-full w-[85%] max-w-sm bg-bookish-cream shadow-2xl border-l border-bookish-purple/20 p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">Filters</h2>
              <button
                className="rounded-md border border-bookish-purple/20 px-3 py-1.5"
                onClick={() => setMobileFiltersOpen(false)}
                autoFocus
                aria-label="Close filters"
              >
                ✕
              </button>
            </div>

            <div className="h-px bg-bookish-purple/10 my-3" />

            {/* Free Books */}
            <button
              onClick={() => setFreeOnly((v) => !v)}
              aria-pressed={freeOnly}
              className={`w-full text-left px-3 py-2 rounded-md mb-4 border ${
                freeOnly
                  ? "bg-bookish-purple text-white border-bookish-purple"
                  : "bg-white text-bookish-purple border-bookish-purple/20 hover:border-bookish-purple/40"
              }`}
            >
              Free Books
            </button>

          {/* Fiction / Non-Fiction toggle */}
          <div className="flex gap-2 mb-3">
            {["Fiction", "Non-Fiction"].map((branch) => {
              const active = genreBranch === branch;
              return (
                <button
                  key={branch}
                  onClick={() => {
                    setGenreBranch(branch);
                    setActiveGenre(null);
                    setSelectedGenre(null);
                    setSelectedSubgenres(new Set());
                  }}
                  className={`flex-1 px-1 py-2 rounded-md text-sm border ${
                    active
                      ? "bg-bookish-purple text-white border-bookish-purple"
                      : "bg-white text-bookish-purple border-bookish-purple/20 hover:border-bookish-purple/40"
                  }`}
                >
                  {branch}
                </button>
              );
            })}
          </div>

            {/* Genres */}
            <h3 className="text-sm font-semibold uppercase tracking-wide text-bookish-purple/70 mb-2">
              Genres
            </h3>
            <div className="space-y-1 max-h-[40vh] overflow-auto pr-1 mb-3">
              {allGenres.length === 0 ? (
                <div className="text-sm text-bookish-purple/60">No genres yet.</div>
              ) : (
                allGenres.map((g) => {
                  const active = activeGenre === g;
                  return (
                    <button
                      key={g}
                      onClick={() => selectGenre(g)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                        active
                          ? "bg-bookish-purple/10 text-bookish-purple font-medium"
                          : "text-bookish-purple/80 hover:bg-bookish-purple/5"
                      }`}
                    >
                      {displayLabel(g)}
                    </button>
                  );
                })
              )}
            </div>

            {activeGenre && (
              <>
                <button
                  onClick={clearGenre}
                  className="text-xs text-bookish-purpleAccent hover:underline mb-3"
                >
                  Clear genre
                </button>

                <div className="space-y-1 max-h-[30vh] overflow-auto pr-1">
                  {subgenresForActive.length === 0 ? (
                    <div className="text-sm text-bookish-purple/60">No subgenres available.</div>
                  ) : (
                    subgenresForActive.map((sg) => {
                      const active = selectedSubgenres.has(sg);
                      return (
                        <button
                          key={sg}
                          onClick={() => toggleSubgenre(sg)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                            active
                              ? "bg-bookish-purple/10 text-bookish-purple font-medium"
                              : "text-bookish-purple/80 hover:bg-bookish-purple/5"
                          }`}
                        >
                          {displayLabel(sg)}
                        </button>
                      );
                    })
                  )}
                </div>
                {selectedSubgenres.size > 0 && (
                  <button
                    onClick={clearSubgenres}
                    className="mt-3 text-xs text-bookish-purpleAccent hover:underline"
                  >
                    Clear subgenres
                  </button>
                )}
              </>
            )}

            <div className="h-px bg-bookish-purple/10 my-4" />

            <div className="flex gap-2">
              <button
                onClick={clearAllFilters}
                className="rounded-md border border-bookish-purple/20 px-3 py-2 text-sm"
              >
                Clear filters
              </button>
              <button
                className="flex-1 rounded-md bg-bookish-purple text-white py-2"
                onClick={() => setMobileFiltersOpen(false)}
              >
                Apply
              </button>
            </div>


          </div>
        </div>
      )}
    </div>
  );
}

/* --- Components --- */

function BookCard({ book }) {
  const sortedFormats = sortFormatsByPrice(book?.formats || []);
  const lowestFormat = sortedFormats[0] || null;
  const previewFormats = sortedFormats.slice(0, 2);

  const productUrl = toProductUrl(book);
  const internalProductPath = toInternalPath(productUrl);
  const authorShopUrl = getAuthorShopUrl(book);

  const ProductLink = ({ className, children }) =>
    internalProductPath ? (
      <Link to={internalProductPath} className={className}>
        {children}
      </Link>
    ) : productUrl ? (
      <a href={productUrl} className={className}>
        {children}
      </a>
    ) : (
      <span className={className}>{children}</span>
    );

  const ebookCover =
    Array.isArray(book.formats)
      ? book.formats.find(
          (f) =>
            String(f?.type || "").toLowerCase().replace(/\s+/g, "") === "ebook" &&
            f?.coverImageUrl
        )?.coverImageUrl
      : "";

  const fallbackCover =
    (book.coverImageUrl || "").trim() ||
    lowestFormat?.coverImageUrl ||
    (Array.isArray(book.formats) &&
      book.formats.find((f) => f.coverImageUrl)?.coverImageUrl);

  const imgSrc = ebookCover || fallbackCover || PLACEHOLDER;

  return (
    <article className="library-book group flex flex-col h-auto">
      <ProductLink className="library-book-cover" >
        <div className="w-full h-56 md:h-64 flex items-end justify-center">
          <img
            src={imgSrc}
            alt={displayLabel(book.title)}
            className="max-h-full max-w-full object-contain p-2 drop-shadow-md transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.src = PLACEHOLDER;
            }}
          />
        </div>
      </ProductLink>

      <BookRating productUrl={productUrl} />
      <div className="library-book-details flex flex-col flex-1">
        <h3 className="mt-0 font-semibold text-[18px] leading-snug line-clamp-2 min-h-[2rem]">
          {productUrl ? (
            <ProductLink className="hover:text-bookish-royal">
              {displayLabel(book.title || "Untitled Book")}
            </ProductLink>
          ) : (
            <span>{displayLabel(book.title || "Untitled Book")}</span>
          )}
        </h3>

        {book.tagline && <p className="library-book-tagline text-sm italic line-clamp-2">{book.tagline}</p>}

        <p className="text-sm font-semibold text-bookish-purple/70 line-clamp-1 min-h-[1.5rem]">
          {authorShopUrl ? (
            <a href={authorShopUrl} className="text-bookish-purple">
              {displayLabel(book.authorName || "Unknown Author")}
            </a>
          ) : (
            displayLabel(book.authorName || "Unknown Author")
          )}
        </p>

        {previewFormats.length > 0 ? (
        <div className="mt-auto space-y-2 pt-5 text-sm">
          {previewFormats.slice(0, 1).map((format, idx) => (
            <div key={`${format?.type || "format"}-${idx}`}>
              <div className="flex leading-snug justify-between">
                <span className="font-medium text-bookish-purple/80">
                  {sortedFormats.length > 1 ? 'From' : displayLabel(format?.type || "Format")}
                </span>

                <span className="font-semibold text-bookish-purple">
                  <AppreciationPrice price={format?.price} format={format?.type} host={book?.subdomain ? book.subdomain.replace(/^https?:\/\//, '').replace(/\/.*$/, '') : book?.productUrl ? new URL(book.productUrl, window.location.origin).hostname : window.location.hostname} />                </span>
              </div>

              {format?.shipping?.freeShipping && (
                <p className="text-xs font-semibold text-bookish-purpleAccent">
                  Free Shipping
                </p>
              )}
            </div>
          ))}
        </div>
        ) : (
          <p className="text-sm text-bookish-purple/60 mt-1">
            Price unavailable
          </p>
        )}

        <div className="pt-3 flex items-center gap-2">
          <TbrButton book={book} productUrl={productUrl} coverImageUrl={imgSrc} compact />

        </div>
      </div>
    </article>
  );
}

function GridSkeleton() {
  return (
    <div className="bg-[#F6EEDD] rounded-3xl p-6 shadow-inner">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5 items-stretch">
        {Array.from({ length: 9 }).map((_, i) => (
          <div
            key={i}
            className="bg-white border border-bookish-purple/10 rounded-xl overflow-hidden animate-pulse"
          >
            <div className="aspect-[4/5] bg-bookish-purple/10" />
            <div className="p-4 space-y-2">
              <div className="h-4 w-3/4 bg-bookish-purple/10 rounded" />
              <div className="h-3 w-1/2 bg-bookish-purple/10 rounded" />
              <div className="h-4 w-1/3 bg-bookish-purple/10 rounded mt-3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, goTo }) {
  if (totalPages <= 1) return null;

  const buttons = [];
  const maxButtons = 7; // compact, but readable
  const half = Math.floor(maxButtons / 2);

  let start = Math.max(1, page - half);
  let end = Math.min(totalPages, start + maxButtons - 1);
  if (end - start + 1 < maxButtons) {
    start = Math.max(1, end - maxButtons + 1);
  }

  for (let p = start; p <= end; p++) {
    buttons.push(
      <button
        key={p}
        onClick={() => goTo(p)}
        className={`min-w-[2.25rem] min-h-[44px] px-2 rounded-md border text-sm ${
          p === page
            ? "bg-bookish-purple text-white border-bookish-purple"
            : "bg-white text-bookish-purple border-bookish-purple/20 hover:border-bookish-purple/40"
        }`}
      >
        {p}
      </button>
    );
  }

  return (
    <div className="mt-6 flex items-center justify-center gap-2">
      <button
        onClick={() => goTo(page - 1)}
        disabled={page <= 1}
        className="min-h-[44px] px-3 rounded-md border bg-white text-sm disabled:opacity-40 border-bookish-purple/20 hover:border-bookish-purple/40"
      >
        Prev
      </button>

      {start > 1 && (
        <>
          <button
            onClick={() => goTo(1)}
            className="min-w-[2.25rem] min-h-[44px] px-2 rounded-md border bg-white text-sm border-bookish-purple/20 hover:border-bookish-purple/40"
          >
            1
          </button>
          {start > 2 && <span className="px-1 text-bookish-purple/60">…</span>}
        </>
      )}

      {buttons}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="px-1 text-bookish-purple/60">…</span>}
          <button
            onClick={() => goTo(totalPages)}
            className="min-w-[2.25rem] min-h-[44px] px-2 rounded-md border bg-white text-sm border-bookish-purple/20 hover:border-bookish-purple/40"
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => goTo(page + 1)}
        disabled={page >= totalPages}
        className="min-h-[44px] px-3 rounded-md border bg-white text-sm disabled:opacity-40 border-bookish-purple/20 hover:border-bookish-purple/40"
      >
        Next
      </button>
    </div>
  );
}
